<template>
  <ModalConfirmation
    hasBlur
    :showModal="showModal"
    title="Delete Scheduled State Change"
  >
    <template v-slot:body>
      <ParagraphBody
        data-test-id="DeleteScheduledEvent__bodyText"
      >
        {{ bodyText }}
      </ParagraphBody>
      <HeadingExtraSmall
        :hasMargin="false"
      >
        Event Details
      </HeadingExtraSmall>
      <ParagraphBody
        data-test-id="DeleteScheduledEvent__eventDetails"
      >
        {{ getEventDetails }}
      </ParagraphBody>
    </template>
    <template v-slot:footer>
      <ButtonGroup
        :isFullWidth="false"
      >
        <Button
          hasNoBackground
          text="cancel"
          @click="closeModal"
        />
        <IconButton
          hasOutline
          icon="delete"
          text="delete"
          type="error"
          @click="deleteStateChange"
        />
      </ButtonGroup>
    </template>
  </ModalConfirmation>
</template>

<script>
import {
  Button,
  ButtonGroup,
  HeadingExtraSmall,
  IconButton,
  ModalConfirmation,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { capitalize } from 'lodash';
import {
  dateDisplayFormat,
  userStateActionMap,
} from 'client/systemusers/constants/SystemUserConstants';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import DateUtil from 'client/util/DateUtil';
import JobApi from 'client/api/v2/JobApi';

const systemUserCollection = 'SystemUserCollection';
const systemUserModel = 'SystemUserModel';

export default {
  name: 'DeleteScheduledEvent',

  components: {
    Button,
    ButtonGroup,
    HeadingExtraSmall,
    IconButton,
    ModalConfirmation,
    ParagraphBody,
  },

  props: {
    action: {
      type: String,
      default: '',
    },

    item: {
      type: Object,
      required: true,
    },

    showModal: Boolean,
  },

  computed: {
    ...mapGetters(systemUserModel, ['displayName']),

    bodyText() {
      let text = '';

      if (this.action) {
        const { noun } = userStateActionMap.get(this.action);

        text = `Are you sure you want to delete this scheduled ${noun}?`;
      }

      return text;
    },

    getEventDetails() {
      const eventDate = DateUtil.customFormatDate(
        this.item.scheduledDate,
        this.$options.dateDisplayFormat,
      );

      return `${capitalize(this.action)} on ${eventDate}`;
    },
  },

  dateDisplayFormat,

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions(systemUserCollection, {
      updateModifiedItemCount: 'updateModifiedItemCount',
      updateNextScheduledEvent: 'updateNextScheduledEvent',
    }),

    closeModal() {
      this.$emit('close');
    },

    async deleteStateChange() {
      if (this.item.scheduledJobId) {
        const { noun } = userStateActionMap.get(this.action);

        try {
          await JobApi.removeScheduledStateChange(this.item.scheduledJobId);
          // Update scheduled events list
          this.updateNextScheduledEvent([this.item._id]);
          // Update aside to refresh scheduled jobs display
          this.updateModifiedItemCount();
          this.triggerSuccessNotification({
            title: 'Removed',
            message: `Scheduled ${noun} removed for ${this.displayName}.`,
          });
        } catch {
          this.triggerErrorNotification({
            title: 'Error',
            message: `Unable to remove scheduled ${noun} for ${this.displayName}.`,
          });
        }
      }

      this.closeModal();
    },
  },
};
</script>

<style module scoped>
.modal.modal {
  --modal-width: 70ch;
  --modal-max-width: 100%;
}

.notificationContainer {
  margin-bottom: var(--jc-spacer);
}
</style>
